import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import './Modal2FAStyle.css';
import { loadBalancerAPI } from '../../api/load-balancer';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { authState } from '../../api/state';

interface TwoFactorAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (code: string) => void;
}

const TwoFactorAuthModal = ({ isOpen, onClose, onVerify }: TwoFactorAuthModalProps) => {
  const [qrCode, setQrCode] = useState('');
  const [providedKey, setProvidedKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showAlternative, setShowAlternative] = useState(false);
  const navigate = useNavigate();
  const authStateValue = useRecoilValue(authState);
  const location = useLocation();

  useEffect(() => {
    const fetchQRCode = async () => {
      try {
        const response = await loadBalancerAPI.apiInstance.getQRCode();
        const qrKeyResponse = response.data.data;
        setProvidedKey(qrKeyResponse.typeKey);
        setQrCode(qrKeyResponse.imageUrl);
      } catch (error) {
        console.error('Failed to fetch QR code:', error);
      }
    };

    if (isOpen) {
      fetchQRCode();
    }
  }, [isOpen]);

  const handleVerify = async () => {
    try {
      const response = await loadBalancerAPI.apiInstance.verifyQrCode(verificationCode, authStateValue.user.id);
      if (response.status === 200) {
        onVerify(verificationCode);
        onClose();
      } else {
        setErrorMessage('The authentication code is incorrect. Please try again.');
      }
    } catch (error) {
      setErrorMessage('The authentication code is incorrect. Please try again.');
    }
  };

  const handleKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      handleVerify();
    }
  };

  const handleCancel = () => {
    setVerificationCode('');
    setErrorMessage('');
    setShowAlternative(false);
    onClose();
    if (location.pathname.includes('two-factor-auth')) {
      navigate('/home');
    }
  };

  return (
    <Dialog className="dialog-factor" open={isOpen} fullWidth maxWidth="lg">
      <DialogTitle className="dialog-title">Setup Google Authenticator</DialogTitle>
      <DialogContent className="display-2fa-modal">
        <div className="section">
          <DialogTitle className="section-step">Step 1</DialogTitle>
          <DialogTitle className="section-title">
            Download & install Google Authenticator on your mobile device
          </DialogTitle>
          <img src="authenticator.png" alt="authenticator" style={{ width: '100%' }} />
        </div>
        <div className="section" style={{ flex: '0 0 34%' }}>
          <DialogTitle className="section-step">Step 2</DialogTitle>
          <DialogTitle className="section-title">Add your account to Google Authenticator</DialogTitle>
          {showAlternative ? (
            <>
              <DialogContentText>
                <b>Can't scan the bar code?</b> <br />
                1. Tap <b>menu</b>, then <b>Set up account</b>. <br />
                2. Tap <b>Enter provided key</b>. <br />
                3. Enter your email address and this key: <br />
                <div className="provided-key">
                  <span style={{ color: 'black' }}>
                    <b>{providedKey}</b>
                  </span>{' '}
                  <br />
                  <span>spaces don't matter</span>
                </div>
                4. Make sure <b>Time based</b> is turned on, and tap <b>Add</b> to finish.
              </DialogContentText>
              <p className="underline-link" onClick={() => setShowAlternative(false)}>
                Scan the QR code?
              </p>
            </>
          ) : (
            <>
              <DialogContentText style={{ fontSize: '14px' }}>
                After clicking the "+" icon in Google Authenticator, use the camera to scan the QR code on the screen.
              </DialogContentText>
              <img src={qrCode} alt="QR Code" style={{ marginTop: '10px', objectFit: 'contain' }} />
              <p className="underline-link" onClick={() => setShowAlternative(true)}>
                Can not scan the QR code?
              </p>
            </>
          )}
        </div>
        <div className="section">
          <DialogTitle className="section-step">Step 3</DialogTitle>
          <DialogTitle className="section-title">Verify your device</DialogTitle>
          <TextField
            required
            margin="dense"
            label="Enter code"
            type="text"
            fullWidth
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            error={!!errorMessage}
            helperText={errorMessage}
            onKeyDown={handleKeyDown}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleVerify} variant="contained" color="primary">
          Verify code and finish
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorAuthModal;
